import { useState } from "react";
import {
  Button,
  Card,
  Center,
  Loader,
  Paper,
  Stack,
  Text,
  Title,
  ThemeIcon,
  Group,
  Badge,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import {
  IconCheck,
  IconMail,
  IconUserPlus,
  IconAlertCircle,
  IconBuilding,
} from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useInvitationByToken, useAcceptInvitation } from "@/hooks/useInvitations";

export const Route = createFileRoute("/invite/$token")({
  component: AcceptInvitationPage,
});

function AcceptInvitationPage() {
  const { token } = Route.useParams();
  const navigate = useNavigate();
  const [accepted, setAccepted] = useState(false);

  const { data, isLoading, isError, error } = useInvitationByToken(token);
  const acceptInvitation = useAcceptInvitation();

  const handleAccept = async () => {
    try {
      const result = await acceptInvitation.mutateAsync(token);
      setAccepted(true);
      notifications.show({
        title: "Welcome to the project!",
        message: `You've been added to ${result.project.name} as ${result.role}`,
        color: "green",
        icon: <IconCheck size={16} />,
      });
      // Navigate to the project after 2 seconds
      setTimeout(() => {
        navigate({ to: "/projects/$projectId", params: { projectId: result.project.id } });
      }, 2000);
    } catch (err) {
      notifications.show({
        title: "Failed to accept invitation",
        message: err instanceof Error ? err.message : "An error occurred",
        color: "red",
        icon: <IconAlertCircle size={16} />,
      });
    }
  };

  if (isLoading) {
    return (
      <Center h="100vh">
        <Stack align="center" gap="md">
          <Loader size="lg" />
          <Text c="dimmed">Loading invitation...</Text>
        </Stack>
      </Center>
    );
  }

  if (isError || !data) {
    return (
      <Center h="100vh">
        <Paper p="xl" withBorder radius="md" w={400}>
          <Stack align="center" gap="md">
            <ThemeIcon size={60} radius="xl" color="red" variant="light">
              <IconAlertCircle size={30} />
            </ThemeIcon>
            <Title order={3}>Invalid Invitation</Title>
            <Text c="dimmed" ta="center">
              {(error as Error)?.message ||
                "This invitation link is invalid, expired, or has already been used."}
            </Text>
            <Button variant="light" onClick={() => navigate({ to: "/" })}>
              Go to Home
            </Button>
          </Stack>
        </Paper>
      </Center>
    );
  }

  const { invitation, project, invitedBy } = data;

  if (invitation.status !== "pending") {
    return (
      <Center h="100vh">
        <Paper p="xl" withBorder radius="md" w={400}>
          <Stack align="center" gap="md">
            <ThemeIcon
              size={60}
              radius="xl"
              color={invitation.status === "accepted" ? "green" : "gray"}
              variant="light"
            >
              {invitation.status === "accepted" ? (
                <IconCheck size={30} />
              ) : (
                <IconAlertCircle size={30} />
              )}
            </ThemeIcon>
            <Title order={3}>
              {invitation.status === "accepted"
                ? "Invitation Already Accepted"
                : invitation.status === "expired"
                  ? "Invitation Expired"
                  : "Invitation Revoked"}
            </Title>
            <Text c="dimmed" ta="center">
              {invitation.status === "accepted"
                ? "You've already joined this project."
                : invitation.status === "expired"
                  ? "This invitation has expired. Please request a new one."
                  : "This invitation has been revoked by the project owner."}
            </Text>
            <Button variant="light" onClick={() => navigate({ to: "/" })}>
              Go to Home
            </Button>
          </Stack>
        </Paper>
      </Center>
    );
  }

  if (accepted) {
    return (
      <Center h="100vh">
        <Paper p="xl" withBorder radius="md" w={400}>
          <Stack align="center" gap="md">
            <ThemeIcon size={60} radius="xl" color="green" variant="light">
              <IconCheck size={30} />
            </ThemeIcon>
            <Title order={3}>Welcome!</Title>
            <Text c="dimmed" ta="center">
              You've successfully joined <strong>{project.name}</strong>.
              Redirecting you to the project...
            </Text>
            <Loader size="sm" />
          </Stack>
        </Paper>
      </Center>
    );
  }

  return (
    <Center h="100vh" bg="gray.0">
      <Card shadow="md" padding="xl" radius="md" w={450}>
        <Stack gap="lg">
          {/* Header */}
          <Stack align="center" gap="xs">
            <ThemeIcon size={60} radius="xl" color="blue" variant="light">
              <IconUserPlus size={30} />
            </ThemeIcon>
            <Title order={2} ta="center">
              You're Invited!
            </Title>
          </Stack>

          {/* Project Info */}
          <Paper p="md" withBorder radius="md" bg="blue.0">
            <Stack gap="sm">
              <Group gap="xs">
                <IconBuilding size={18} style={{ color: "var(--mantine-color-blue-6)" }} />
                <Text size="sm" c="dimmed">
                  Project
                </Text>
              </Group>
              <Title order={4}>{project.name}</Title>
            </Stack>
          </Paper>

          {/* Invitation Details */}
          <Stack gap="sm">
            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Invited as
              </Text>
              <Badge
                color={invitation.role === "owner" ? "blue" : "green"}
                variant="light"
                size="lg"
              >
                {invitation.role}
              </Badge>
            </Group>

            {invitedBy && (
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  Invited by
                </Text>
                <Text fw={500}>{invitedBy.name}</Text>
              </Group>
            )}

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Email
              </Text>
              <Group gap="xs">
                <IconMail size={14} style={{ color: "var(--mantine-color-gray-6)" }} />
                <Text size="sm">{invitation.email}</Text>
              </Group>
            </Group>

            <Group justify="space-between">
              <Text size="sm" c="dimmed">
                Expires
              </Text>
              <Text size="sm" c="orange">
                {new Date(invitation.expiresAt).toLocaleDateString()}
              </Text>
            </Group>
          </Stack>

          {/* Role Description */}
          <Paper p="sm" withBorder radius="md" bg="gray.0">
            <Text size="xs" c="dimmed">
              {invitation.role === "owner"
                ? "As an owner, you'll have full access to this project including budget, cost codes, and team management."
                : "As a contributor, you'll be able to submit daily logs and photos. Your submissions are isolated - you can only see and edit your own work."}
            </Text>
          </Paper>

          {/* Accept Button */}
          <Button
            size="lg"
            fullWidth
            leftSection={<IconCheck size={18} />}
            onClick={handleAccept}
            loading={acceptInvitation.isPending}
          >
            Accept Invitation
          </Button>

          <Text size="xs" c="dimmed" ta="center">
            By accepting, you agree to join this project with the specified role.
          </Text>
        </Stack>
      </Card>
    </Center>
  );
}
